import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useCallback, useEffect, useState } from "react";
import { fetchBuildings, fetchBuildingsOfCompany } from "../../../api/building";
import { associateDeviceToCompany } from "../../../api/device";
import { fetchPartnerships, PartnershipView } from "../../../api/partnership";
import { ENUMDEVICETYPE_OPTION } from "../../../generated/enums";
import { Building } from "../../../generated/types";
import { useAccountStorage } from "../../../hooks/useAccountStorage";
import { isAssistant } from "../../../utils/roles";
import { Button } from "../../common/Button";
import { FormInput, FormSelect } from "../../common/FormLayout";
import { Modal, ModalActions } from "../../common/Modal";
import { PasswordField } from "../../common/PasswordField";

/**
 * Types de produits qui DOIVENT être installés dans un bâtiment.
 *
 * Miroir de `link.BuildingRequiredForType` côté DATA — c'est le backend qui fait foi (400
 * sinon). La liste est dupliquée ici uniquement pour marquer le champ requis AVANT
 * l'envoi : ce sont les régulateurs legacy, vigebox excepté.
 */
const BUILDING_REQUIRED_TYPES = ["avitouch", "avibox", "labelbox"];

/**
 * Types qui ne s'installent PAS dans un bâtiment.
 *
 * Le vigebox est un produit à part : il ne remonte pas de mesures par bâtiment, lui en
 * proposer un n'aurait aucun sens. Distinct de « facultatif » — un serenity, lui, peut
 * légitimement être installé quelque part.
 */
const BUILDING_EXCLUDED_TYPES = ["vigebox"];

/**
 * Types identifiés par leur code UUID, sans mot de passe : le serenity n'est pas un
 * régulateur legacy, il ne porte ni numéro de série ni mot de passe imprimés.
 */
const UUID_TYPES = ["serenity"];

type AssociateDeviceModalProps = {
  isOpen: boolean;
  onClose: () => void;
  /** Rappelé après une association réussie, pour rafraîchir la liste. */
  onAssociated: () => void;
  /** Client présélectionné (installateur depuis la fiche d'un élevage). */
  companyId?: number;
};

/**
 * Association d'un produit, hors espace admin.
 *
 * Distincte de `LinkDeviceModal` (admin), qui liste toutes les entreprises et tous les
 * comptes via des routes admin. Deux parcours ici, selon le rôle :
 *
 *  - **éleveur** : aucune entreprise à choisir, c'est forcément la sienne. Le backend
 *    ÉCRASE `id_company` avec celle de la session — le champ n'est donc même pas envoyé.
 *  - **installateur / groupement** : l'entreprise cible est choisie parmi les clients
 *    avec qui il est en partenariat accepté.
 *
 * Les comptes éleveurs ne sont jamais demandés : c'est le backend qui, une fois le
 * produit rattaché, crée les liens vers tous les éleveurs de l'entreprise.
 */
export const AssociateDeviceModal = ({
  isOpen,
  onClose,
  onAssociated,
  companyId,
}: AssociateDeviceModalProps) => {
  const { getAccountStorage } = useAccountStorage();
  const accountType = getAccountStorage()?.type;

  // L'éleveur associe à sa propre entreprise : pas de sélecteur, pas de chargement.
  const isBreeder = accountType === "breeder";

  const [clients, setClients] = useState<PartnershipView[]>([]);
  const [loadingClients, setLoadingClients] = useState(true);
  const [buildings, setBuildings] = useState<Building[]>([]);
  const [loadingBuildings, setLoadingBuildings] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const [idCompany, setIdCompany] = useState("");
  const [idBuilding, setIdBuilding] = useState("");
  const [uuid, setUuid] = useState("");
  const [type, setType] = useState("");
  const [password, setPassword] = useState("");

  const buildingRequired = BUILDING_REQUIRED_TYPES.includes(type);
  const uuidOnly = UUID_TYPES.includes(type);
  // Tant qu'aucun type n'est choisi, on n'affiche rien : proposer un bâtiment qu'il
  // faudrait retirer au choix suivant ferait sauter le formulaire sous l'utilisateur.
  const buildingShown = type !== "" && !BUILDING_EXCLUDED_TYPES.includes(type);

  const reset = () => {
    setIdCompany("");
    setIdBuilding("");
    setUuid("");
    setType("");
    setPassword("");
    setBuildings([]);
    setError(null);
    setSuccess(false);
  };

  const loadClients = useCallback(async () => {
    if (isBreeder) {
      setLoadingClients(false);
      return;
    }
    setLoadingClients(true);
    // `role=provider` : les partenariats où je suis le prestataire, donc mes clients.
    // `status=accepted` : une demande en attente ne donne aucun droit.
    // `canAssociate` : un collaborateur ne doit voir que les clients où son chef lui a
    // accordé le droit d'associer — sinon il choisit une entreprise pour se faire
    // refuser en 403. Sans effet pour un installateur.
    const result = await fetchPartnerships({
      role: "provider",
      status: "accepted",
      canAssociate: true,
    });
    setClients(result.partnerships);
    setError(result.error);
    setLoadingClients(false);
  }, [isBreeder]);

  useEffect(() => {
    if (isOpen) {
      reset();
      if (companyId) setIdCompany(String(companyId));
      loadClients();
    }
  }, [isOpen, loadClients, companyId]);

  // Bâtiments de l'entreprise CIBLE. Rechargés à chaque changement de client : les
  // bâtiments d'un client n'ont rien à voir avec ceux d'un autre, et proposer les anciens
  // mènerait droit au 403 du backend (bâtiment hors de l'entreprise cible).
  //
  // L'éleveur passe par la route scopée session (`fetchBuildings`), la seule dont il a
  // besoin : le backend lui impose de toute façon sa propre entreprise.
  useEffect(() => {
    if (!isOpen) return;

    setIdBuilding("");
    setBuildings([]);

    if (!isBreeder && !idCompany) return;

    let cancelled = false;
    setLoadingBuildings(true);

    const load = isBreeder
      ? fetchBuildings().then((r) => ({
          buildings: r.Buildings,
          error: r.error,
        }))
      : fetchBuildingsOfCompany(Number(idCompany));

    load.then((result) => {
      // La modale a pu être fermée, ou le client changé, pendant la requête : écrire
      // alors écraserait la liste du client suivant.
      if (cancelled) return;
      setBuildings(result.buildings);
      if (result.error) setError(result.error);
      setLoadingBuildings(false);
    });

    return () => {
      cancelled = true;
    };
  }, [isOpen, isBreeder, idCompany]);

  const close = () => {
    reset();
    onClose();
  };

  const handleSubmit = async () => {
    if (
      (!isBreeder && !idCompany) ||
      !uuid.trim() ||
      !type ||
      (!uuidOnly && !password)
    ) {
      setError("Tous les champs sont obligatoires.");
      return;
    }

    // Le backend refuse en 400, mais autant le dire avant l'aller-retour — et nommer le
    // champ fautif, ce que le code HTTP seul ne permet pas.
    if (buildingRequired && !idBuilding) {
      setError("Ce type de produit doit être installé dans un bâtiment.");
      return;
    }

    setSubmitting(true);
    setError(null);
    const result = await associateDeviceToCompany({
      // Éleveur : valeur ignorée par le backend, qui impose son entreprise de session.
      id_company: isBreeder ? 0 : Number(idCompany),
      uuid: uuid.trim(),
      type,
      password_legacy: password,
      // 0 = aucun bâtiment : accepté par le backend pour serenity et vigebox.
      id_building: Number(idBuilding) || 0,
    });
    setSubmitting(false);

    if (!result.success) {
      setError(result.error);
      return;
    }
    setSuccess(true);
    onAssociated();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={close}
      title="Associer un produit"
      icon="plus"
      maxWidth="md"
    >
      {success ? (
        <div className="text-center py-4">
          <div className="mb-4 text-secondary">
            <FontAwesomeIcon icon="check-circle" size="3x" />
          </div>
          <p className="text-lg mb-2 text-default-text">Produit associé</p>
          <p className="text-sm text-grey-readable mb-6">
            {isBreeder
              ? "Il est désormais accessible à vous-même et aux autres éleveurs de votre entreprise."
              : "Il est désormais accessible aux éleveurs de cette entreprise."}
          </p>
          <ModalActions>
            <Button onClick={close}>Fermer</Button>
          </ModalActions>
        </div>
      ) : (
        <div className="py-2 flex flex-col gap-4">
          {error && (
            <div
              role="alert"
              className="p-3 rounded-lg bg-red-50 text-red-700 text-sm"
            >
              {error}
            </div>
          )}

          {loadingClients ? (
            <p className="text-grey-readable">Chargement de vos clients…</p>
          ) : !isBreeder && clients.length === 0 ? (
            <div className="py-6 text-center text-grey-readable">
              <FontAwesomeIcon icon="handshake" size="2x" className="mb-3" />
              <p className="text-default-text font-oxaniumMedium">
                Aucun client
              </p>
              <p className="text-sm mt-1">
                {isAssistant(accountType)
                  ? "Aucun partenariat ne vous est ouvert. Demandez à votre responsable de vous autoriser à associer des produits."
                  : "Nouez un partenariat depuis la page Partenaires pour pouvoir associer un produit chez un client."}
              </p>
            </div>
          ) : (
            <>
              {/* L'éleveur n'a qu'une entreprise : lui demander de la choisir n'aurait
                  aucun sens, et le backend impose de toute façon celle de sa session. */}
              {!isBreeder && (
                <FormSelect
                  label="Entreprise cliente"
                  required
                  value={idCompany}
                  onChange={(e) => setIdCompany(e.target.value)}
                  // FormSelect ajoute lui-même une option vide en tête.
                  options={clients.map((c) => ({
                    value: String(c.company.id),
                    label: c.company.company_name,
                  }))}
                />
              )}

              {/* Le type précède le bâtiment : c'est lui qui décide si ce dernier est
                  obligatoire. L'ordre du formulaire suit celui des décisions. */}
              <FormSelect
                label="Type de produit"
                required
                value={type}
                onChange={(e) => {
                  setType(e.target.value);
                  // Un bâtiment choisi avant de basculer sur un type qui n'en prend pas
                  // resterait dans l'état et partirait quand même au backend. Même chose
                  // pour un mot de passe saisi avant de choisir un serenity.
                  if (BUILDING_EXCLUDED_TYPES.includes(e.target.value)) {
                    setIdBuilding("");
                  }
                  if (UUID_TYPES.includes(e.target.value)) {
                    setPassword("");
                  }
                }}
                options={ENUMDEVICETYPE_OPTION.map((o) => ({
                  value: o.value,
                  label: o.value,
                }))}
              />

              {/* Bâtiment d'installation. Masqué tant qu'aucun type n'est choisi et pour
                  les types qui ne s'installent pas (vigebox) ; requis pour les legacy
                  restants, qui n'ont aucun autre ancrage vers leurs mesures ; facultatif
                  pour un serenity, qui peut être installé sans y être tenu. */}
              {!buildingShown ? null : loadingBuildings ? (
                <p className="text-sm text-grey-readable">
                  Chargement des bâtiments…
                </p>
              ) : buildings.length === 0 ? (
                // Sans bâtiment, un type qui en exige un ne peut pas être associé : mieux
                // vaut le dire ici que laisser l'utilisateur buter sur le bouton.
                <p className="text-sm text-grey-readable">
                  {buildingRequired
                    ? "Aucun bâtiment n'existe pour cette entreprise. Créez-en un avant d'associer ce type de produit."
                    : "Aucun bâtiment disponible : le produit sera associé sans installation."}
                </p>
              ) : (
                <FormSelect
                  label="Bâtiment d'installation"
                  required={buildingRequired}
                  value={idBuilding}
                  onChange={(e) => setIdBuilding(e.target.value)}
                  options={buildings.map((b) => ({
                    value: String(b.id),
                    label: b.name,
                  }))}
                />
              )}

              {/* Sur un régulateur legacy, « numéro de série » : c'est ce qui est imprimé
                  sur le matériel, personne ne parle d'UUID en élevage. Le serenity, lui,
                  n'a que son code UUID — et pas de mot de passe. */}
              <FormInput
                label={uuidOnly ? "Code UUID" : "Numéro de série"}
                required
                value={uuid}
                onChange={(e) => setUuid(e.target.value)}
                placeholder={uuidOnly ? "" : "132845901"}
              />

              {!uuidOnly && (
                <PasswordField
                  label="Mot de passe du produit"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              )}

              <p className="text-sm text-grey-readable">
                {isBreeder
                  ? "Une fois associé, le produit sera accessible à vous-même et aux autres éleveurs de votre entreprise."
                  : "Une fois associé, le produit sera accessible aux éleveurs de l'entreprise cliente."}
                {buildingRequired &&
                  " Ce type de régulateur remonte ses mesures par bâtiment : son installation est donc obligatoire."}
              </p>

              <ModalActions>
                <Button onClick={close} variant="secondary">
                  Annuler
                </Button>
                <Button onClick={handleSubmit} disabled={submitting}>
                  {submitting ? "Association…" : "Associer"}
                </Button>
              </ModalActions>
            </>
          )}
        </div>
      )}
    </Modal>
  );
};
